import { useParams } from "react-router-dom";
import { useViewResourcesSuspense } from "@swivu/client-api";
import { AccordionV2, ErrorBoundary, groupBy, Spinner } from "@swivu/shared-ui";
import {
  TeamScheduleFilter,
  TeamScheduleGrid,
  TeamScheduleProvider,
  useTeamSchedule
} from "@swivu/team-schedule";
import { GroupTeamSchedule } from "@swivu/team-schedule/src/groupTeamSchedule.js";
import { useSettings } from "../hooks/useSettings.js";
import SuspenseWithDelay from "@swivu/shared-ui/src/components/SuspenseWithDelay.js";

const ViewEvents = () => {
  const { timezone } = useSettings();
  const { id } = useParams();
  return (
    <SuspenseWithDelay
      fallback={
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      }
    >
      <ErrorBoundary>
        <TeamScheduleProvider
          isPublic={true}
          viewId={Number(id)}
          timezone={timezone}
          slideoutContainerId="slideoutContainer"
        >
          <ViewEventTeamSchedule />
        </TeamScheduleProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

const ViewEventTeamSchedule = () => {
  const { timezone } = useSettings();
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const { data } = useViewResourcesSuspense({
    variables: {
      id: id ?? "",
      slug: slug ?? "",
      api: "public"
    }
  });
  const resources = data?.map((r) => ({
    id: r.id,
    name: r.label ?? r.job_title ?? r.display_name,
    group_value: r._group_value ?? "N/A"
  }));

  const { gridData } = useTeamSchedule();

  const mappedEventsToResources = mapEventsToResources(
    resources,
    gridData as GroupTeamSchedule<"resource">,
    slug ?? ""
  );

  // if resources only has N/A, then show the grid data as is
  if (resources.every((r) => r.group_value === "N/A")) {
    return (
      <div className=" @container mx-auto max-w-screen-lg px-4 pt-2">
        {/* <pre>{JSON.stringify(mappedEventsToResources, null, 2)}</pre> */}
        <TeamScheduleFilter />
        <TeamScheduleGrid
          timeZone={timezone}
          customData={mappedEventsToResources}
        />
      </div>
    );
  }

  const grouped = groupBy(mappedEventsToResources, "groupValue");

  return (
    <div className=" @container mx-auto max-w-screen-lg">
      {/* <pre>{JSON.stringify(grouped, null, 2)}</pre> */}
      <TeamScheduleFilter />
      <AccordionV2.PersistentRoot persistKey="view-events-accordion relative">
        {Object.entries(grouped).map(([label, group]) => {
          return (
            <AccordionV2.Item key={label} value={label} className="">
              <AccordionV2.Trigger className="w-full  ">
                <span className="text-lg font-semibold">{label}</span>
              </AccordionV2.Trigger>
              <AccordionV2.Content>
                <div>
                  {group.map(
                    (
                      item: GroupTeamSchedule<"resource">[number] & {
                        groupValue: string;
                      }
                    ) => (
                      <TeamScheduleGrid
                        key={item.resourceId}
                        timeZone={timezone}
                        customData={[item]}
                      />
                    )
                  )}
                </div>
              </AccordionV2.Content>
            </AccordionV2.Item>
          );
        })}
      </AccordionV2.PersistentRoot>
    </div>
  );
};

const mapEventsToResources = (
  resources: {
    id: number;
    name: string | null | undefined;
    group_value: string;
  }[],
  gridData: GroupTeamSchedule<"resource"> | null,
  slug: string
) => {
  if (!gridData) {
    return [];
  }
  return resources.map((resource) => ({
    groupValue: resource.group_value,
    resourceId: resource.id,
    rowLabel: resource.name ?? resource.group_value,
    rowLink: `/schedule/${slug}/resource/${resource.id}?titles=${
      resource.name || ""
    }`,
    dates: gridData.find((g) => g.resourceId === resource.id)?.dates ?? []
  }));
};

export default ViewEvents;
